import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentEnterprise, useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { GeneralPortfolioProductSubscriptionType } from '@mews-commander/data-access';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { LocalizedTypography, useEnterpriseBuildLocation } from '@mews-commander/ui';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { Grid, Stack } from '@optimus-web/core';
import { isCurrentEnterprisePortfolio } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import BreadcrumbList, { Breadcrumbs } from '../../../components/BreadcrumbList';
import { Authentication } from '../Authentication';
import { DeviceAuthConfiguration } from '../DeviceAuthConfiguration/DeviceAuthConfiguration';
import { useOwnerId } from '../queries/useOwnerId';
import { ScimConfiguration } from '../ScimConfiguration/ScimConfiguration';
import { SsoConfiguration } from '../SsoConfiguration/SsoConfiguration';

const DEFAULT_TEMPLATE_COLUMNS = `${1 / 3}fr`;
const TABLET_TEMPLATE_COLUMNS = `${2 / 3}fr`;
const PHONE_TEMPLATE_COLUMNS = '1fr';

export const SecurityDashboard = () => {
    const breadcrumbs = useBreadcrumbs();
    const hasDeviceAuthorization = useFeatureFlag(FeatureFlag.deviceAuthorizationEnabled);
    const { hasSSOConfiguration, hasUserProvisioning } = useSecurityDashboardFlags();

    return (
        <>
            <BreadcrumbList breadcrumbs={breadcrumbs} alignment="start" />
            <Grid
                templateColumns={DEFAULT_TEMPLATE_COLUMNS}
                tabletOnly={{ templateColumns: TABLET_TEMPLATE_COLUMNS }}
                largePhoneMax={{ templateColumns: PHONE_TEMPLATE_COLUMNS }}
                rowGap="500"
            >
                <SecurityDashboardSection title={TextKey.Authentication}>
                    <Authentication />
                    {hasDeviceAuthorization && <DeviceAuthConfiguration />}
                    {hasSSOConfiguration && <SsoConfiguration />}
                </SecurityDashboardSection>

                {hasUserProvisioning && (
                    <SecurityDashboardSection title={TextKey.UserProvisioning}>
                        <ScimConfiguration />
                    </SecurityDashboardSection>
                )}
            </Grid>
        </>
    );
};

const useBreadcrumbs = (): Breadcrumbs => {
    const buildLocation = useEnterpriseBuildLocation();
    const ownerId = useOwnerId();
    const currentEnterpriseId = useCurrentEnterpriseId();
    const { id, name } = useEnterprise(ownerId || currentEnterpriseId);

    return useMemo(
        () => [
            {
                text: name,
                url: buildLocation({ to: 'Enterprise/Detail/$enterpriseId', params: { enterpriseId: id } }).href,
            },
            {
                active: true,
                text: <LocalizedText textKey={TextKey.Security} />,
            },
        ],
        [buildLocation, id, name]
    );
};

function useSecurityDashboardFlags() {
    const showSsoSelfService: boolean = useFeatureFlag(FeatureFlag.ssoSelfServiceUiEnabled);
    const { portfolioProductSubscriptionTypes } = useCurrentEnterprise();
    const hasSsoAccess = !!portfolioProductSubscriptionTypes?.includes(
        GeneralPortfolioProductSubscriptionType.SingleSignOn
    );
    const isPortfolio = useSelector(isCurrentEnterprisePortfolio);

    const hasSSOConfiguration = showSsoSelfService && hasSsoAccess && isPortfolio;
    const hasUserProvisioning = hasSsoAccess && isPortfolio;

    return {
        hasSSOConfiguration,
        hasUserProvisioning,
    };
}

const SecurityDashboardSection = ({ title, children }: React.PropsWithChildren<{ title: Mews.TextKey }>) => (
    <Stack vertical spacing="300">
        <LocalizedTypography textKey={title} textStyle="titleMediumStrong" as="div" />
        {children}
    </Stack>
);
